/**
 * DSD 셀에 실린 DL·기간·항목 배정을 눈으로 확인하는 검토 모드 스위치 (beta 전용).
 *
 * 고른 모드는 `body` 의 `data-region-review` 값으로만 나가고, 실제 표시는
 * `styles/global.css` 가 셀의 `data-dl-title` / `data-dl-index` / `data-period` /
 * `data-line-item-id` 와 그 속성의 **부재**를 보고 그린다. 렌더러는 모드를 몰라도 되고, 화면과 정적
 * 다운로드본이 같은 것을 본다.
 *
 * 항목(line_item)은 `all` 에 넣지 않고 자기 모드로만 본다 — DL 과 면색·이름표 자리를
 * 모두 공유해서 겹쳐 켜면 어느 축의 색인지 알 수 없다.
 *
 * DL 과 기간을 따로 볼 수 있어야 한다 — 둘 다 켜면 좁은 셀에서 이름표와 기간 표시가
 * 겹쳐 읽기 어렵고, 확인하려는 축은 보통 한 번에 하나다. DL 모드는 대사 매칭 면색을
 * 덮으므로(면색 축이 하나뿐이다) 매칭 결과를 봐야 할 때는 끄고 본다.
 */

import { Fragment, useEffect, useState } from "react";

import { BetaFeatureOnly } from "@/components/beta-feature";
import { REGION_REVIEW_ATTR, type RegionReviewMode } from "@/utils/dom-attrs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/styles/ui/select";

const MODE_ITEMS: { value: RegionReviewMode; label: string }[] = [
  { value: "off", label: "배정 표시 끔" },
  { value: "dl", label: "DL 배정" },
  { value: "period", label: "기간 배정" },
  { value: "line-item", label: "항목 배정" },
  { value: "member", label: "멤버 배정" },
  { value: "all", label: "DL·기간 배정" },
];

export interface MemberAxisOption {
  id: string;
  label: string;
}

interface Props {
  /** 지금 표에 있는 member 축들. 비면 "멤버 배정" 항목을 안 보여준다. */
  memberAxes?: MemberAxisOption[];
  /** 선택된 축 — 렌더러가 그 축만 셀에 싣는다. */
  reviewAxisId?: string | null;
  onReviewAxisChange?: (axisId: string | null) => void;
  /**
   * false 면 beta 게이트 없이 항상 보인다. 주석 레퍼런스 검색의 매핑 보기처럼 배정을
   * 보는 것이 화면의 목적인 곳에서 쓴다. 대사 화면은 기본값(beta 전용) 그대로다.
   */
  betaOnly?: boolean;
}

export const DsdRegionReviewToggle = ({
  memberAxes = [],
  reviewAxisId,
  onReviewAxisChange,
  betaOnly = true,
}: Props) => {
  const [mode, setMode] = useState<RegionReviewMode>("off");

  useEffect(() => {
    const { body } = document;
    if (mode === "off") body.removeAttribute(REGION_REVIEW_ATTR);
    else body.setAttribute(REGION_REVIEW_ATTR, mode);

    return () => body.removeAttribute(REGION_REVIEW_ATTR);
  }, [mode]);

  // member 모드로 들어오면 축이 하나는 선택돼 있어야 한다 — 안 그러면 표가 통째로
  // 회색(미배정)이라 모드를 켠 것이 고장으로 보인다.
  useEffect(() => {
    if (mode !== "member" || !memberAxes.length) return;
    if (!memberAxes.some((axis) => axis.id === reviewAxisId)) {
      onReviewAxisChange?.(memberAxes[0].id);
    }
  }, [mode, memberAxes, reviewAxisId, onReviewAxisChange]);

  const items = MODE_ITEMS.filter(
    (item) => item.value !== "member" || memberAxes.length > 0,
  );

  const Gate = betaOnly ? BetaFeatureOnly : Fragment;

  return (
    <Gate>
      <div title="DL·항목 배정은 면색+이름표(대사 매칭 색 대신), 기간 배정은 우상단 글자(당기 파랑·전기 빨강)로 표시한다. 회색 면은 그 축의 미배정, 빨간 ?는 기간 미배정.">
        <Select
          value={mode}
          onValueChange={(value) => setMode(value as RegionReviewMode)}
        >
          <SelectTrigger className="h-7 w-[124px] gap-1 px-2 label-2-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {mode === "member" && memberAxes.length > 1 && (
        <Select
          value={reviewAxisId ?? memberAxes[0].id}
          onValueChange={(value) => onReviewAxisChange?.(value)}
        >
          <SelectTrigger className="h-7 w-[150px] gap-1 px-2 label-2-medium">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {memberAxes.map((axis) => (
              <SelectItem key={axis.id} value={axis.id}>
                {axis.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Gate>
  );
};
